import React, { useState, useEffect, useCallback} from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { 
  DollarSign, Plus, Search, Eye, Filter, RefreshCw,
  CheckCircle2, Clock, XCircle, CreditCard, Loader2
} from 'lucide-react';
import toast from 'react-hot-toast';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const VendorPaymentList = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [payments, setPayments] = useState([]);
  const [filteredPayments, setFilteredPayments] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('ALL');

  useEffect(() => {
    loadPayments();
  }, []);


  const loadPayments = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${BACKEND_URL}/api/ap/payments`);
      setPayments(response.data);
    } catch (error) {
      console.error('Error loading payments:', error);
      toast.error('Failed to load vendor payments');
    } finally {
      setLoading(false);
    }
  };

  const filterPayments = useCallback(() => {
    let filtered = [...payments];

    if (statusFilter !== 'ALL') {
      filtered = filtered.filter(p => p.status === statusFilter);
    }

    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(p =>
        p.payment_number?.toLowerCase().includes(term) ||
        p.vendor_id?.toLowerCase().includes(term) ||
        p.vendor_name?.toLowerCase().includes(term)
      );
    }

    setFilteredPayments(filtered);
  }, [payments, searchTerm, statusFilter]);

  useEffect(() => {
    filterPayments();
  }, [filterPayments]);

  const getStatusColor = (status) => {
    const colors = {
      'DRAFT': 'bg-gray-100 text-gray-800',
      'APPROVED': 'bg-orange-100 text-orange-800',
      'SCHEDULED': 'bg-yellow-100 text-yellow-800',
      'POSTED': 'bg-green-100 text-green-800',
      'COMPLETED': 'bg-green-100 text-green-800',
      'REVERSED': 'bg-red-100 text-red-800',
      'CANCELLED': 'bg-gray-100 text-gray-800'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'DRAFT': return <Clock className="w-4 h-4" />;
      case 'POSTED': return <CheckCircle2 className="w-4 h-4" />;
      case 'COMPLETED': return <CheckCircle2 className="w-4 h-4" />;
      case 'REVERSED': return <XCircle className="w-4 h-4" />;
      default: return <Clock className="w-4 h-4" />;
    }
  };

  const formatCurrency = (amount, currency = 'USD') => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency
    }).format(amount);
  };

  return (
    <Layout>
      <div className="p-6 max-w-7xl mx-auto" data-testid="vendor-payment-list">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
              <DollarSign className="w-7 h-7 text-emerald-600" />
              Vendor Payments
            </h1>
            <p className="text-sm text-gray-500">Outgoing payments to vendors (Accounts Payable)</p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => navigate('/ap/open-items')}>
              <CreditCard className="w-4 h-4 mr-2" />
              Open Items
            </Button>
            <Button onClick={() => navigate('/ap/payment-proposal')} className="bg-emerald-600 hover:bg-emerald-700">
              <Plus className="w-4 h-4 mr-2" />
              Payment Run (F110)
            </Button>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-lg shadow-sm border p-4 mb-6">
          <div className="flex flex-wrap gap-4 items-end">
            <div className="flex-1 min-w-[200px] max-w-md">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                <Input
                  placeholder="Search by payment number, vendor..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Filter className="w-4 h-4 text-gray-400" />
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-md text-sm"
              >
                <option value="ALL">All Status</option>
                <option value="DRAFT">Draft</option>
                <option value="APPROVED">Approved</option>
                <option value="POSTED">Posted</option>
                <option value="REVERSED">Reversed</option>
              </select>
            </div>
            <Button variant="outline" size="sm" onClick={loadPayments}>
              <RefreshCw className="w-4 h-4 mr-1" />
              Refresh
            </Button>
          </div>
        </div>

        {/* Results */}
        <div className="bg-white rounded-lg shadow-sm border">
          {loading ? (
            <div className="p-8 text-center">
              <Loader2 className="w-12 h-12 animate-spin text-emerald-600 mx-auto mb-4" />
              <p className="text-gray-600">Loading vendor payments...</p>
            </div>
          ) : filteredPayments.length === 0 ? (
            <div className="p-8 text-center">
              <DollarSign className="w-12 h-12 mx-auto mb-4 text-gray-300" />
              <p className="text-gray-600">No vendor payments found</p>
              <Button onClick={() => navigate('/ap/payment-proposal')} className="mt-4" variant="outline">
                Create Payment Run
              </Button>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Payment #</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Vendor</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Method</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Invoice Amt</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Discount</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Net Payment</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Payment Date</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {filteredPayments.map((payment) => (
                    <tr key={payment.payment_number} className="hover:bg-gray-50">
                      <td className="px-4 py-3">
                        <span className="font-medium text-emerald-600">{payment.payment_number}</span>
                      </td>
                      <td className="px-4 py-3">
                        <p className="text-gray-900 font-medium">{payment.vendor_id}</p>
                        <p className="text-xs text-gray-500 truncate max-w-[150px]">{payment.vendor_name}</p>
                      </td>
                      <td className="px-4 py-3">
                        <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(payment.status)}`}>
                          {getStatusIcon(payment.status)}
                          {payment.status}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-gray-600">
                        {payment.payment_method_name || payment.payment_method}
                      </td>
                      <td className="px-4 py-3 text-right font-medium">
                        {formatCurrency(payment.total_invoice_amount, payment.currency)}
                      </td>
                      <td className="px-4 py-3 text-right text-green-600">
                        {payment.total_discount_taken > 0 ? `-${formatCurrency(payment.total_discount_taken, payment.currency)}` : '-'}
                      </td>
                      <td className="px-4 py-3 text-right font-bold text-emerald-700">
                        {formatCurrency(payment.net_payment_amount, payment.currency)}
                      </td>
                      <td className="px-4 py-3 text-gray-600">{payment.payment_date}</td>
                      <td className="px-4 py-3">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => navigate(`/ap/payments/${payment.payment_number}`)}
                          className="text-emerald-600 hover:text-emerald-700"
                        >
                          <Eye className="w-4 h-4 mr-1" />
                          View
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Summary */}
        <div className="mt-4 text-sm text-gray-500">
          Showing {filteredPayments.length} of {payments.length} payments
        </div>
      </div>
    </Layout>
  );
};

export default VendorPaymentList;
